import React, { useCallback, useRef, useEffect, useLayoutEffect, useMemo, useState, memo } from 'react';
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  Connection,
  EdgeTypes,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  useStoreApi,
  ViewportPortal,
  SelectionMode,
  OnNodesChange,
  EdgeChange,
  Edge,
  NodePositionChange,
  NodeTypes,
  Viewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CustomEdge } from './edges';
import { EdgeParticlesCanvas } from './edges/EdgeParticlesCanvas';
import { GenerateNode } from './nodes';
import { DetailPageNode } from './nodes/DetailPageNode';
import GroupNode from './nodes/GroupNode';
import { SelectionToolbar } from './SelectionToolbar';
import { InverseScaleBox } from './nodes/parts/InverseScaleBox';
import { NodeErrorBoundary } from '../ui/ErrorBoundary';
import { useNodeStore } from '../../stores/nodeStore';
import { useGroupStore } from '../../stores/groupStore';
import { useCanvasStore } from '../../stores/canvasStore';
import { useSelectionStore, setMagneticZoom } from '../../stores/selectionStore';
import { useEdgeActiveStore } from '../../stores/edgeActiveStore';
import { useLayerEditStore } from '../../stores/layerEditStore';
import { AppNode, NodeType, isGroupNode } from '../../types';
import { NODE_TYPE_CONFIG } from '../../config/nodeTypeConfig';
import { config } from '../../config';
import { DetailPageStage } from '../../types';
import { deriveStage } from './nodes/hooks/useDetailPageState';
import { toast } from 'sonner';
import { rafThrottle } from '../../utils/throttle';
import { MiniMapToolbar } from './MiniMapToolbar';
import { useProjectStore } from '../../stores/projectStore';
import { projectApi } from '../../services/projectApi';
import { normalizeAssetUrl } from '../../utils/assetUtils';
import { findClosestAspectRatio } from '../../utils/aspectRatioUtils';
import { useCopyDrag } from '../../hooks/useCopyDrag';
import { useExternalFileUpload } from '../../hooks/useExternalFileUpload';
import { markFilePasteTakeover } from '../../utils/pasteCoordination';
import { CopyDragOverlay } from './CopyDragOverlay';

// 模块级 resize 状态：任何 resize 路径（GroupNode / TextNode）开始时置 true，结束置 false
// FlowEditor 的 useEffect[nodes] 读取此 ref，resize 期间跳过全量重建 localNodes
// 避免所有节点 wrapper 新建 → ReactFlow diff 所有节点 → backdrop-filter 重合成 → 频闪
const isResizingAnyNode = { current: false };
export const startNodeResize = () => { isResizingAnyNode.current = true; };
export const endNodeResize = () => { isResizingAnyNode.current = false; };

const connectionEdgeId = (c: { from: string; to: string; targetHandle?: string }) =>
  `${c.from}-${c.to}${c.targetHandle ? `-${c.targetHandle}` : ''}`;

// 节点视觉尺寸常量（与 GenerateNode / DetailPageNode 保持一致）
const SHORT_EDGE = 420;
const DEFAULT_ASPECT_RATIO = '16:9';
const DIRECTOR_WIDTH = 728;
const DIRECTOR_HEIGHT = 720;
const LAYER_EDIT_WIDTH = 728;
const LAYER_EDIT_HEIGHT = 720;
const DETAIL_PAGE_WIDTH = 728;
/** 详情页节点兜底高度：高度自适应后仅用于 xyflow measured 未就绪时（首帧）的框选命中计算 */
const DETAIL_PAGE_HEIGHTS: Record<DetailPageStage, number> = {
  [DetailPageStage.CONFIG]: 512,
  [DetailPageStage.PLANNING]: 720,
  [DetailPageStage.DESIGNING]: 720,
  [DetailPageStage.CONSUME]: 880,
};

// 基于节点类型和数据，返回节点的视觉尺寸（用户看到的卡片尺寸）
// 用于框选命中计算，避免 measured 尺寸被浮动 UI 撑大导致误命中
function getNodeVisualSize(node: AppNode, measuredHeight?: number): { width: number; height: number } {
  if (node.type === NodeType.GROUP) {
    return { width: node.width ?? 280, height: node.height ?? 200 };
  }
  if (node.type === NodeType.DETAIL_PAGE) {
    const detailData = (node.data ?? {}) as any;
    const stage = deriveStage(detailData);
    // 高度自适应：优先取 xyflow 实测高度（卡片内容决定），measured 未就绪时回退兜底常量
    const fallbackHeight = DETAIL_PAGE_HEIGHTS[stage] ?? 640;
    return { width: DETAIL_PAGE_WIDTH, height: measuredHeight ?? fallbackHeight };
  }
  if (node.type === NodeType.DIRECTOR) {
    return { width: DIRECTOR_WIDTH, height: DIRECTOR_HEIGHT };
  }
  if (node.type === NodeType.LAYER_EDIT) {
    return { width: LAYER_EDIT_WIDTH, height: LAYER_EDIT_HEIGHT };
  }
  // GenerateNode 逻辑（与 GenerateNode.tsx 一致）
  const data = node.data as any;
  if (data?.data?.nodeSize) {
    return {
      width: data.data.nodeSize.width || 640,
      height: data.data.nodeSize.height || 440,
    };
  }
  if (node.height) {
    return { width: node.width || SHORT_EDGE, height: node.height };
  }
  const ratio = data?.data?.aspectRatio || DEFAULT_ASPECT_RATIO;
  const [w, h] = ratio.split(':').map(Number);
  const isLandscape = w >= h;
  const width = isLandscape ? SHORT_EDGE * w / h : SHORT_EDGE;
  const height = isLandscape ? SHORT_EDGE : SHORT_EDGE * h / w;
  return { width, height };
}

type FlowNode = {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  data: Record<string, unknown> & { progress?: string };
  selected?: boolean;
  width?: number;
  height?: number;
  measured?: { width?: number; height?: number };
  style?: React.CSSProperties;
  parentId?: string;
  extent?: 'parent' | [[number, number], [number, number]];
  expandParent?: boolean;
};

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

// P0-1: 从 CustomEdge 移至 FlowEditor 层，BFS 只执行 1 次，结果通过 data.isActive 共享
// 计算"同支线"节点集合：
// - 下游：从选中节点出发，沿 from→to 方向 BFS，包含所有子孙分支
// - 上游：从选中节点出发，沿 to→from 方向反向 BFS，包含所有能到达选中节点的祖先
//   （并行入边全部纳入；不指向选中节点的兄弟分支因反向遍历自然被排除）
function getConnectedNodes(
  startNodeId: string,
  edges: Array<{ from: string; to: string }>
): Set<string> {
  const connected = new Set<string>();
  connected.add(startNodeId);

  // 下游：沿 from→to 方向 BFS，包含所有可达下游节点（含所有分支）
  const downstreamQueue = [startNodeId];
  while (downstreamQueue.length > 0) {
    const currentId = downstreamQueue.shift()!;
    for (const edge of edges) {
      if (edge.from === currentId && !connected.has(edge.to)) {
        connected.add(edge.to);
        downstreamQueue.push(edge.to);
      }
    }
  }

  // 上游：沿 to→from 方向反向 BFS，包含所有能到达选中节点的祖先
  // 并行入边全部纳入；不指向选中节点的兄弟分支（如 A→B→C 中选中 C 时的 B→F）
  // 因为反向遍历只走"指向当前节点"的边，自然不会扩散到兄弟分支
  const upstreamQueue = [startNodeId];
  while (upstreamQueue.length > 0) {
    const currentId = upstreamQueue.shift()!;
    for (const edge of edges) {
      if (edge.to === currentId && !connected.has(edge.from)) {
        connected.add(edge.from);
        upstreamQueue.push(edge.from);
      }
    }
  }

  return connected;
}

interface FlowEditorProps {
  onNodeAction: (id: string, prompt?: string) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onNodeDelete: (id: string) => void;
  onAddNode: (type: NodeType, position?: { x: number; y: number }, initialData?: any) => string | undefined;
  onExtractImage?: (nodeId: string, image: string) => void;
  onGeneratePanorama?: (sourceNodeId: string, params: {
    prompt: string;
    quality: import('../../types').PanoramaQuality;
    referenceImageUrl?: string;
    mode: 'txt2panorama' | 'img2panorama';
  }) => void;
  onGenerateMultiAngle?: (sourceNodeId: string, params: {
    prompt: string;
    imageUrl: string;
    model?: string;
    aspectRatio?: string;
    multiAngleParams: import('../../types').MultiAngleParams;
  }) => void;
  onGenerateLighting?: (sourceNodeId: string, params: {
    imageUrl: string;
    model?: string;
    lightingParams: import('../../types').LightingParams;
    lightingRequest: { lighting_style: string };
  }) => void;
  onToggleChat?: () => void;
  onOpenChat?: () => void;
  onOpenDirector?: (nodeId: string) => void;
  onOpenLayerEdit?: (nodeId: string) => void;
  onOpenScript?: (nodeId: string) => void;
  /** 智能剪辑：携带输入栏会话上下文打开全屏面板（§4.1.3） */
  onOpenSmartEdit?: (nodeId: string, session?: import('@/types/smartEdit').SmartEditSession | null) => void;
  // 文档 4.2：拖动/连接 saveHistory 触发 + Alt+拖动复制 pasteAt
  saveHistory?: () => void;
  pasteAt?: (sourceNodes: AppNode[], sourceConns: Array<{ from: string; to: string }>, delta: { deltaX: number; deltaY: number }) => string[];
  // 画布右键菜单：撤销/粘贴（EditorLayout 内 useUndoHistory/useClipboard 私有实例透传）
  onUndo?: () => void;
  onPaste?: () => void;
}

/** 供父组件（EditorLayout 侧边栏等）命令式调用的编辑器能力（ref 句柄） */
export interface FlowEditorHandle {
  /** 打开画布媒体上传的文件选择，选完后在当前视口中心新建带素材的节点（与双击菜单「上传」同链路） */
  openUploadPicker: () => void;
  /** 平滑聚焦（视口平移动画，模式同打灯面板聚焦）到指定节点中心，用于外部建节点后把画面带到新节点 */
  focusNode: (nodeId: string) => void;
  /** 当前画布可视区中心的画布坐标（供 Provider 外的建节点入口取落点）；画布未就绪时返回 null */
  getViewportCenter: () => { x: number; y: number } | null;
}

interface NodePickerState {
  visible: boolean;
  x: number;
  y: number;
  sourceNodeId?: string;
}

// #5: 提取为模块级常量，避免每次渲染重建
// 设计稿 231:712「添加节点」：四组入口、组间分隔线，与右键菜单二级面板同稿；
// 「资产」暂无 NodeType，按设计稿渲染禁用态；「上传」走素材选择链路
const NODE_PICKER_GROUPS: { items: { key: string; icon: string; name: string; nodeType?: NodeType }[] }[] = [
  {
    items: [
      { key: 'text', icon: '/icons/node-picker/text.svg', name: '文本', nodeType: NodeType.TEXT_NODE },
      { key: 'image', icon: '/icons/node-picker/image.svg', name: '图片', nodeType: NodeType.IMAGE_GENERATOR },
      { key: 'detail-page', icon: '/icons/node-picker/detail-page.svg', name: '详情页', nodeType: NodeType.DETAIL_PAGE },
      { key: 'layer-edit', icon: '/icons/node-picker/layer-edit.svg', name: '图层编辑', nodeType: NodeType.LAYER_EDIT },
    ],
  },
  {
    items: [
      { key: 'video', icon: '/icons/node-picker/video.svg', name: '视频', nodeType: NodeType.VIDEO_GENERATOR },
      { key: 'video-compose', icon: '/icons/node-picker/video-compose.svg', name: '智能剪辑', nodeType: NodeType.VIDEO_COMPOSE },
      { key: 'director', icon: '/icons/node-picker/director.svg', name: '导演台', nodeType: NodeType.DIRECTOR },
      { key: 'script', icon: '/icons/node-picker/script.svg', name: '脚本', nodeType: NodeType.SCRIPT_NODE },
    ],
  },
  {
    items: [
      { key: 'audio', icon: '/icons/node-picker/audio.svg', name: '音频', nodeType: NodeType.AUDIO_GENERATOR },
    ],
  },
  {
    items: [
      { key: 'asset', icon: '/icons/node-picker/asset.svg', name: '资产' },
      { key: 'upload', icon: '/icons/node-picker/upload.svg', name: '上传' },
    ],
  },
];

// #1: memo + 自定义比较函数，防止父组件重渲染时牵连菜单
const NodePickerMenu = memo(({
  visible, x, y, onSelect, onClose, onUploadClick,
}: {
  visible: boolean;
  x: number;
  y: number;
  onSelect: (type: NodeType) => void;
  onClose: () => void;
  onUploadClick: () => void;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [actualSize, setActualSize] = useState<{ w: number; h: number }>({ w: 200, h: 528 });

  // 渲染后测量实际尺寸
  useLayoutEffect(() => {
    if (!visible || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setActualSize({ w: rect.width, h: rect.height });
    }
  }, [visible]);

  // #4: useMemo 同步计算位置，消除 useEffect 二次渲染
  const adjustedPos = useMemo(() => {
    if (!visible) return { x, y };
    const MENU_WIDTH = actualSize.w;
    const MENU_HEIGHT = actualSize.h;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const PADDING = 16;
    let ax = x;
    let ay = y;
    if (ax + MENU_WIDTH > vw - PADDING) ax = vw - PADDING - MENU_WIDTH;
    if (ay + MENU_HEIGHT > vh - PADDING) ay = vh - PADDING - MENU_HEIGHT;
    if (ax < PADDING) ax = PADDING;
    if (ay < PADDING) ay = PADDING;
    return { x: ax, y: ay };
  }, [visible, x, y, actualSize]);

  // click-outside 关闭
  useEffect(() => {
    if (!visible) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] w-[200px] bg-[#1D1D1D] rounded-xl shadow-2xl p-1 select-none"
      style={{
        left: adjustedPos.x,
        top: adjustedPos.y,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 标题：设计稿 231:712 「添加节点」 12px #666666 */}
      <div className="px-3 pt-3 pb-2 text-[12px] leading-[12px] text-[#666666]">添加节点</div>
      {NODE_PICKER_GROUPS.map((group, gi) => (
        <div key={gi}>
          {gi > 0 && <div className="mx-3 my-[5px] h-px bg-white/5" />}
          <div className="flex flex-col gap-[2px]">
            {group.items.map((item) => {
              // 「资产」暂无 NodeType，按设计稿渲染禁用态；「上传」走素材选择链路
              const disabled = !item.nodeType && item.key !== 'upload';
              return (
                <div
                  key={item.key}
                  onClick={() => {
                    if (disabled) return;
                    if (item.nodeType) {
                      onSelect(item.nodeType);
                    } else {
                      // 上传项：点击弹出素材选择，选择后由父组件创建带素材的节点
                      onUploadClick();
                    }
                    onClose();
                  }}
                  className={`flex items-center gap-2.5 h-10 px-3 rounded-lg cursor-default transition-colors ${disabled ? '' : 'hover:bg-[#2D2D2D]'}`}
                >
                  <img src={item.icon} alt={item.name} width={16} height={16} className="shrink-0" />
                  <span className={`text-[14px] leading-none ${disabled ? 'text-[#666666]' : 'text-white'}`}>{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}, (prev, next) => {
  // 自定义比较：visible/x/y 变化才重渲染，onSelect/onClose 引用变化不触发
  return prev.visible === next.visible && prev.x === next.x && prev.y === next.y;
});

// 画布空白处右键菜单（设计稿 231:692 一级 + 231:712 添加节点二级）
// 一级：添加节点（悬停右弹二级）/ 粘贴 ⌘V / 重做 ⌘⇧Z（禁用）/ 撤销 ⌘Z
const CTX_MENU_SIZE = { w: 240, h: 181 };
const CTX_SUBMENU_SIZE = { w: 200, h: 528 };

const CTX_ADD_NODE_GROUPS: { items: { key: string; icon: string; name: string; nodeType?: NodeType }[] }[] = [
  {
    items: [
      { key: 'text', icon: '/icons/context-menu/ctx-text.svg', name: '文本', nodeType: NodeType.TEXT_NODE },
      { key: 'image', icon: '/icons/context-menu/ctx-image.svg', name: '图片', nodeType: NodeType.IMAGE_GENERATOR },
      { key: 'detail-page', icon: '/icons/context-menu/ctx-detail-page.svg', name: '详情页', nodeType: NodeType.DETAIL_PAGE },
      { key: 'layer-edit', icon: '/icons/context-menu/ctx-layer-edit.svg', name: '图层编辑', nodeType: NodeType.LAYER_EDIT },
    ],
  },
  {
    items: [
      { key: 'video', icon: '/icons/context-menu/ctx-video.svg', name: '视频', nodeType: NodeType.VIDEO_GENERATOR },
      { key: 'video-edit', icon: '/icons/context-menu/ctx-video-edit.svg', name: '智能剪辑', nodeType: NodeType.VIDEO_COMPOSE },
      { key: 'director', icon: '/icons/context-menu/ctx-director.svg', name: '导演台', nodeType: NodeType.DIRECTOR },
      { key: 'script', icon: '/icons/context-menu/ctx-script.svg', name: '脚本', nodeType: NodeType.SCRIPT_NODE },
    ],
  },
  {
    items: [
      { key: 'audio', icon: '/icons/context-menu/ctx-audio.svg', name: '音频', nodeType: NodeType.AUDIO_GENERATOR },
    ],
  },
  {
    items: [
      { key: 'asset', icon: '/icons/context-menu/ctx-asset.svg', name: '资产' },
      { key: 'upload', icon: '/icons/context-menu/ctx-upload.svg', name: '上传' },
    ],
  },
];

const CanvasContextMenu = memo(({
  visible, x, y, onSelectNode, onUploadClick, onPaste, onUndo, onClose,
}: {
  visible: boolean;
  x: number;
  y: number;
  onSelectNode: (type: NodeType) => void;
  onUploadClick: () => void;
  onPaste: () => void;
  onUndo: () => void;
  onClose: () => void;
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  // 每次打开重置二级面板
  useEffect(() => {
    if (visible) setSubmenuOpen(false);
  }, [visible, x, y]);

  // 视口防溢出（尺寸固定，直接用设计稿常量收敛，同 NodePickerMenu adjustedPos 模式）
  const adjustedPos = useMemo(() => {
    if (!visible) return { x, y };
    const PADDING = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let ax = x;
    let ay = y;
    if (ax + CTX_MENU_SIZE.w > vw - PADDING) ax = vw - PADDING - CTX_MENU_SIZE.w;
    if (ay + CTX_MENU_SIZE.h > vh - PADDING) ay = vh - PADDING - CTX_MENU_SIZE.h;
    if (ax < PADDING) ax = PADDING;
    if (ay < PADDING) ay = PADDING;
    return { x: ax, y: ay };
  }, [visible, x, y]);

  // 二级面板默认右弹，右侧放不下翻到左侧；底部放不下上移
  const submenuPos = useMemo(() => {
    if (!visible) return { x: 0, y: 0 };
    const GAP = 8;
    const PADDING = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const right = adjustedPos.x + CTX_MENU_SIZE.w + GAP;
    const sx = right + CTX_SUBMENU_SIZE.w > vw - PADDING
      ? adjustedPos.x - CTX_SUBMENU_SIZE.w - GAP
      : right;
    const sy = adjustedPos.y + CTX_SUBMENU_SIZE.h > vh - PADDING
      ? vh - PADDING - CTX_SUBMENU_SIZE.h
      : adjustedPos.y;
    return { x: Math.max(PADDING, sx), y: Math.max(PADDING, sy) };
  }, [visible, adjustedPos]);

  // click-outside 关闭（root 同时包住一级与二级面板）
  useEffect(() => {
    if (!visible) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [visible, onClose]);

  if (!visible) return null;

  const menuItemClass = 'flex items-center justify-between h-10 px-3 rounded-lg cursor-default transition-colors hover:bg-[#2D2D2D]';

  return (
    <div ref={rootRef} className="fixed inset-0 z-[1000] pointer-events-none">
      {/* 一级面板：240×181 #1D1D1D 圆角12 */}
      <div
        className="absolute pointer-events-auto w-[240px] bg-[#1D1D1D] rounded-xl shadow-2xl p-1 select-none"
        style={{ left: adjustedPos.x, top: adjustedPos.y }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className={menuItemClass}
          onMouseEnter={() => setSubmenuOpen(true)}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[14px] leading-none text-white">添加节点</span>
          <img src="/icons/context-menu/ctx-arrow-right.svg" alt="" width={16} height={16} className="shrink-0" />
        </div>
        <div className="mx-3 my-1 h-px bg-white/5" />
        <div className="flex flex-col gap-[2px]">
          <div
            className={menuItemClass}
            onMouseEnter={() => setSubmenuOpen(false)}
            onClick={() => { onPaste(); onClose(); }}
          >
            <span className="text-[14px] leading-none text-white">粘贴</span>
            <span className="text-[14px] leading-none text-[#666666]">⌘V</span>
          </div>
          {/* 重做：暂无 redo 实现，按设计稿渲染禁用态（opacity 0.25） */}
          <div
            className="flex items-center justify-between h-10 px-3 rounded-lg cursor-default opacity-25"
            onMouseEnter={() => setSubmenuOpen(false)}
          >
            <span className="text-[14px] leading-none text-white">重做</span>
            <span className="text-[14px] leading-none text-[#666666]">⌘⇧Z</span>
          </div>
          <div
            className={menuItemClass}
            onMouseEnter={() => setSubmenuOpen(false)}
            onClick={() => { onUndo(); onClose(); }}
          >
            <span className="text-[14px] leading-none text-white">撤销</span>
            <span className="text-[14px] leading-none text-[#666666]">⌘Z</span>
          </div>
        </div>
      </div>
      {/* 二级面板：200×528，四组节点入口，图标 16×16 @x=16 文本 @x=42 */}
      {submenuOpen && (
        <div
          className="absolute pointer-events-auto w-[200px] bg-[#1D1D1D] rounded-xl shadow-2xl p-1 select-none"
          style={{ left: submenuPos.x, top: submenuPos.y }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="px-3 pt-3 pb-2 text-[12px] leading-[12px] text-[#666666]">添加节点</div>
          {CTX_ADD_NODE_GROUPS.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && <div className="mx-3 my-[5px] h-px bg-white/5" />}
              <div className="flex flex-col gap-[2px]">
                {group.items.map((item) => {
                  const disabled = !item.nodeType && item.key !== 'upload';
                  return (
                    <div
                      key={item.key}
                      className={`flex items-center gap-2.5 h-10 px-3 rounded-lg cursor-default transition-colors ${disabled ? '' : 'hover:bg-[#2D2D2D]'}`}
                      onClick={() => {
                        if (disabled) return;
                        if (item.nodeType) {
                          onSelectNode(item.nodeType);
                        } else {
                          onUploadClick();
                        }
                        onClose();
                      }}
                    >
                      <img src={item.icon} alt={item.name} width={16} height={16} className="shrink-0" />
                      <span className={`text-[14px] leading-none ${disabled ? 'text-[#666666]' : 'text-white'}`}>{item.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  // 自定义比较：visible/x/y 变化才重渲染（同 NodePickerMenu）
  return prev.visible === next.visible && prev.x === next.x && prev.y === next.y;
});

// P0-2：nodeTypes 模块级化（根修 React Flow #002 警告）
// 组件内 useCallback/useMemo 的缓存会被 React Fast Refresh 在热更新时按设计重置：
// 每次编辑相关源码 → NodeWithZoom 身份变化 → customNodeTypes 重建 → React Flow
// 重新注册全部节点类型并告警（大画布伴随明显卡顿）。提升到模块级后对象身份永久
// 稳定，与热更新重渲染彻底解耦；运行期回调经两个模块级 ref 在 FlowEditorInner
// 渲染期回填最新值（同 isResizingAnyNode 模式）。
type EditorNodeCallbacks = Pick<FlowEditorProps, 'onNodeAction' | 'onNodeUpdate' | 'onNodeDelete' | 'onExtractImage' | 'onAddNode' | 'onGeneratePanorama' | 'onGenerateMultiAngle' | 'onGenerateLighting' | 'onToggleChat' | 'onOpenChat' | 'onOpenDirector' | 'onOpenLayerEdit' | 'onOpenScript' | 'onOpenSmartEdit'>;
const callbacksRef: { current: EditorNodeCallbacks } = {
  current: {
    onNodeAction: () => {},
    onNodeUpdate: () => {},
    onNodeDelete: () => {},
    onAddNode: () => undefined,
  },
};
const bindCopyDragRef: { current: (nodeId: string) => (e: React.MouseEvent) => void } = { current: () => () => {} };

const NodeWithZoom = (props: Record<string, unknown>) => {
  const { selected, id, type, position, data, width, height, dragging, ...rest } = props;
  const nodeName = NODE_TYPE_CONFIG[type as NodeType]?.name || '节点';
  const cb = callbacksRef.current;
  // 文档 5.6：节点 wrapper 捕获阶段拦截 Alt+左键，进入拖拽复制模式
  // display: contents 不生成盒子，不影响 React Flow 节点布局/尺寸，但能捕获事件
  const handleCopyMouseDown = (e: React.MouseEvent) => {
    bindCopyDragRef.current(String(id))(e);
  };

  if (type === NodeType.DETAIL_PAGE) {
    return (
      <div onMouseDownCapture={handleCopyMouseDown} style={{ display: 'contents' }}>
        <NodeErrorBoundary nodeId={String(id)} nodeName={nodeName}>
          <DetailPageNode
            data={data as AppNode}
            selected={Boolean(selected)}
            dragging={Boolean(dragging)}
            onNodeUpdate={cb.onNodeUpdate}
            onAddNode={cb.onAddNode}
            onNodeAction={cb.onNodeAction}
          />
        </NodeErrorBoundary>
      </div>
    );
  }

  return (
    <div onMouseDownCapture={handleCopyMouseDown} style={{ display: 'contents' }}>
      <NodeErrorBoundary nodeId={String(id)} nodeName={nodeName}>
        <GenerateNode
          data={data as AppNode}
          selected={Boolean(selected)}
          dragging={Boolean(dragging)}
          onNodeAction={cb.onNodeAction}
          onNodeUpdate={cb.onNodeUpdate}
          onNodeDelete={cb.onNodeDelete}
          onExtractImage={cb.onExtractImage}
          onAddNode={cb.onAddNode}
          onGeneratePanorama={cb.onGeneratePanorama}
          onGenerateMultiAngle={cb.onGenerateMultiAngle}
          onGenerateLighting={cb.onGenerateLighting}
          onToggleChat={cb.onToggleChat}
          onOpenChat={cb.onOpenChat}
          onOpenDirector={cb.onOpenDirector}
          onOpenLayerEdit={cb.onOpenLayerEdit}
          onOpenScript={cb.onOpenScript}
          onOpenSmartEdit={cb.onOpenSmartEdit}
        />
      </NodeErrorBoundary>
    </div>
  );
};

const customNodeTypes = {
  [NodeType.PROMPT_INPUT]: NodeWithZoom,
  [NodeType.MEDIA_UPLOAD]: NodeWithZoom,
  [NodeType.IMAGE_GENERATOR]: NodeWithZoom,
  [NodeType.VIDEO_GENERATOR]: NodeWithZoom,
  [NodeType.AUDIO_GENERATOR]: NodeWithZoom,
  [NodeType.IMAGE_EDITOR]: NodeWithZoom,
  [NodeType.PANORAMA_GENERATOR]: NodeWithZoom,
  [NodeType.DIRECTOR]: NodeWithZoom,
  [NodeType.LAYER_EDIT]: NodeWithZoom,
  [NodeType.SCRIPT_NODE]: NodeWithZoom,
  [NodeType.TEXT_NODE]: NodeWithZoom,
  [NodeType.DETAIL_PAGE]: NodeWithZoom,
  [NodeType.VIDEO_COMPOSE]: NodeWithZoom,
  [NodeType.GROUP]: GroupNode,
} as NodeTypes;

const FlowEditorInner = React.forwardRef<FlowEditorHandle, FlowEditorProps>(({
  onNodeAction,
  onNodeUpdate,
  onNodeDelete,
  onAddNode,
  onExtractImage,
  onGeneratePanorama,
  onGenerateMultiAngle,
  onGenerateLighting,
  onToggleChat,
  onOpenChat,
  onOpenDirector,
  onOpenLayerEdit,
  onOpenScript,
  onOpenSmartEdit,
  saveHistory,
  pasteAt,
  onUndo,
  onPaste,
}, ref) => {
  const nodes = useNodeStore((s) => s.nodes);
  const connections = useNodeStore((s) => s.connections);
  const selectedNodeIdsSet = useSelectionStore((s) => s.selectedNodeIds);
  const selectedNodeIds = useMemo(() => Array.from(selectedNodeIdsSet), [selectedNodeIdsSet]);
  const setSelectedNodeIds = useSelectionStore((s) => s.setSelectedNodeIds);
  const setActiveGroup = useGroupStore((s) => s.setActiveGroup);
  const createGroup = useGroupStore((s) => s.createGroup);
  const checkNodeEnterGroup = useCanvasStore((s) => s.checkNodeEnterGroup);
  const checkNodeExitGroup = useCanvasStore((s) => s.checkNodeExitGroup);
  // P1: defaultViewport 只在 ReactFlow 挂载时生效一次，改为只读一次的 ref
  // 之前用响应式订阅 viewportX/Y/Zoom，每次 canvasStore 更新都触发 FlowEditorInner 重渲染
  // 但 defaultViewport 的变化对已挂载的 ReactFlow 无效，完全多余
  // 流式自适应：1920 基准缩放体系已退役，视口原样还原（原小屏 zoom 乘算分支删除）
  const initialViewportRef = useRef(() => {
    const state = useCanvasStore.getState();
    return { x: state.x, y: state.y, zoom: state.zoom };
  });
  const initialViewport = initialViewportRef.current();
  const setViewport = useCanvasStore((s) => s.setViewport);
  const reactFlowInstance = useReactFlow();
  // 文档 5.6：Alt+拖动复制 hook（方案 B，封装完整拖拽逻辑）
  const copyDrag = useCopyDrag({
    saveHistory: saveHistory || (() => {}),
    pasteAt: pasteAt || (() => []),
    screenToFlowPosition: reactFlowInstance.screenToFlowPosition,
  });
  // P0-2：回填模块级 ref（nodeTypes 已模块级化，见 FlowEditorInner 上方的模块级定义）
  bindCopyDragRef.current = copyDrag.bindNodeMouseDown;
  // 外部文件（拖拽/粘贴）统一上传管线：两个入口共用一个实例（方案 §4.1）
  const { uploadFilesAtScreen } = useExternalFileUpload({
    onAddNode,
    screenToFlowPosition: reactFlowInstance.screenToFlowPosition,
  });

  // 系统 paste 监听：剪贴板携带文件时接管为上传，落点取视口中心（方案 §4.3）
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      // 图层编辑器全屏覆盖期间不接管（与 useKeyboardShortcuts 的 enabled 条件对齐）
      if (useLayerEditStore.getState().isOpen) return;
      // 输入框/富文本编辑器内不接管（保护 PromptEditor、脚本面板、提示词输入等现有粘贴行为）
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length === 0) return; // 无文件 → 放行给内部节点粘贴（useKeyboardShortcuts 延迟回调裁决）

      e.preventDefault();
      markFilePasteTakeover();
      uploadFilesAtScreen(files, { x: window.innerWidth / 2, y: window.innerHeight / 2 }, 'paste');
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [uploadFilesAtScreen]);
  const [nodePicker, setNodePicker] = useState<NodePickerState>({ visible: false, x: 0, y: 0 });
  const nodePickerRef = useRef(nodePicker);
  nodePickerRef.current = nodePicker;
  // 节点菜单「上传」用的隐藏 file input
  const pickerFileInputRef = useRef<HTMLInputElement>(null);
  // 保存上传触发时的菜单位置（onClose 会清零 nodePicker，需独立保存）
  const uploadPositionRef = useRef<{ x: number; y: number; sourceNodeId?: string }>({ x: 0, y: 0 });
  // 画布右键上下文菜单（添加节点/粘贴/撤销），与旧节点选择面板互斥
  const [ctxMenu, setCtxMenu] = useState<{ visible: boolean; x: number; y: number }>({ visible: false, x: 0, y: 0 });
  const ctxMenuRef = useRef(ctxMenu);
  ctxMenuRef.current = ctxMenu;
  const [isSelecting, setIsSelecting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const connectingSourceRef = useRef<string | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nodesRef = useRef(nodes);
  const selectedNodeIdsRef = useRef(selectedNodeIds);
  // 同步跟踪最新的 selected 节点 ID，避免 handleSelectionEnd 读取 localNodes 闭包过时
  // 解决框选结束时 onSelectionEnd 先于 localNodes 状态更新被调用，导致残留之前选中的节点
  const latestSelectedIdsRef = useRef<Set<string>>(new Set(selectedNodeIds));
  // 框选起点（画布坐标），用于 handleSelectionEnd 重新计算命中节点
  const selectionStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const draggedNodeIdsRef = useRef<string[]>([]);
  const pendingPositionUpdatesRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const rafIdRef = useRef<number | null>(null);
  // P1-1: 拖拽停止后跳过一次全量重建，因为 localNodes 已在拖拽过程中更新
  const skipNextNodesRebuildRef = useRef(false);
  // resize 期间跳过全量重建：resize 只改变单个节点的 width/height，
  // 通过 onNodesChange 局部更新 localNodes，避免 buildFlowNodes 为所有节点新建 wrapper
  // 否则 ReactFlow 会对所有节点做 diff/协调，触发 backdrop-filter 重新合成 → 频闪
  const resizingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // 订阅 groupStore.resizingGroupId：GroupNode resize 开始/结束时同步 resizingRef
  // TextNode resize 通过 callbacksRef.onResizeStart/onResizeEnd 同步（见 useSelfWriteEditor）
  useEffect(() => {
    let prev = useGroupStore.getState().resizingGroupId;
    resizingRef.current = prev !== null;
    return useGroupStore.subscribe((state) => {
      const current = state.resizingGroupId;
      if (current !== prev) {
        prev = current;
        resizingRef.current = current !== null;
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // 全局订阅 ReactFlow 内部 store 的 transform（实时无 debounce），同步 magneticZoom 和 --flow-zoom
  // 方案 7：magneticZoom 移出 Zustand，改为模块级变量，避免每次 set 触发 200+ 订阅器通知
  // 方案 5：--flow-zoom CSS 变量 RAF 节流，合并同一帧内的多次更新，对齐显示器刷新率
  // 方案 3：MagneticHandle 全局订阅，替代每个 handle 独立订阅（订阅器从 200 减至 1）
  // 之前订阅 canvasStore.zoom，但 canvasStore 通过 60ms debounce 更新，
  // 缩放过程中 ReactFlow transform 与 canvasStore.zoom 不同步 → MagneticHandle 计算 snapOffset/zoom 用旧 zoom
  // → 位置错乱 → registerHandle set → 闪烁
  // 现在直接订阅 ReactFlow 内部 store，实时同步 magneticZoom
  const rfStore = useStoreApi();
  useEffect(() => {
    const root = document.documentElement;
    let zoomRafId: number | null = null;
    let pendingZoom = rfStore.getState().transform[2];
    // 方案 3：handle 位置批量更新 RAF，合并所有 handle 的 getBoundingClientRect 调用
    let handleRafId: number | null = null;

    const applyZoom = (zoom: number) => {
      // 方案 7：magneticZoom 是模块级变量，直接调用 setMagneticZoom，不经过 Zustand
      setMagneticZoom(zoom);
      // 方案 5：--flow-zoom CSS 变量 RAF 节流，合并同一帧内的多次更新
      pendingZoom = zoom;
      if (zoomRafId !== null) return;
      zoomRafId = requestAnimationFrame(() => {
        zoomRafId = null;
        root.style.setProperty('--flow-zoom', String(pendingZoom));
      });
    };

    // 方案 3：批量更新所有 handle 的缓存位置
    // 通过 data-handle-id 属性查询所有 handle DOM，在 RAF 内统一调用 registerHandle
    // registerHandle 内部使用方案 2 的 RAF 批量更新，每帧最多 1 次 set
    const scheduleHandlePositionsUpdate = () => {
      if (handleRafId !== null) return;
      handleRafId = requestAnimationFrame(() => {
        handleRafId = null;
        const handles = document.querySelectorAll('[data-handle-id]');
        const { registerHandle } = useSelectionStore.getState();
        handles.forEach(el => {
          const handleId = el.getAttribute('data-handle-id');
          if (!handleId) return;
          const rect = el.getBoundingClientRect();
          registerHandle(handleId, rect.left + rect.width / 2, rect.top + rect.height / 2);
        });
      });
    };

    applyZoom(pendingZoom);
    const unsub = rfStore.subscribe((state) => {
      applyZoom(state.transform[2]);
      // 方案 3：transform 变化时批量更新 handle 位置
      scheduleHandlePositionsUpdate();
    });
    return () => {
      unsub();
      if (zoomRafId !== null) cancelAnimationFrame(zoomRafId);
      if (handleRafId !== null) cancelAnimationFrame(handleRafId);
    };
  }, [rfStore]);

  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds;
  }, [selectedNodeIds]);

  const buildFlowNodes = useCallback((appNodes: AppNode[], existingLocalNodes?: FlowNode[]) => {
    const nodeIds = new Set(appNodes.map(n => n.id));
    const nodeMap = new Map(appNodes.map(n => [n.id, n]));
    const existingMap = existingLocalNodes
      ? new Map(existingLocalNodes.map(ln => [ln.id, ln]))
      : null;
    const currentSelectedIds = useSelectionStore.getState().selectedNodeIds;
    return appNodes.map(n => {
      const existing = existingMap?.get(n.id);
      const hasValidParent = n.parentNode && nodeIds.has(n.parentNode);
      let posX = n.x;
      let posY = n.y;
      if (hasValidParent) {
        const parent = nodeMap.get(n.parentNode!);
        if (parent) {
          posX = n.x - parent.x;
          posY = n.y - parent.y;
        }
      }
      const isGroup = isGroupNode(n);
      return {
        id: n.id,
        type: n.type,
        position: { x: posX, y: posY },
        data: isGroup
          ? { ...n.data, nodeWidth: n.width, nodeHeight: n.height } as unknown as Record<string, unknown>
          : n as unknown as Record<string, unknown>,
        selected: currentSelectedIds.has(n.id),
        // 保留上一次的 DOM 测量结果：全量重建若丢失 measured，重建后的窗口期内拖拽会触发 React Flow #015
        ...(existing?.measured ? { measured: existing.measured } : {}),
        // 方案 B fallback：透传 AppNode 已有的 width/height，避免 measured 为空时框选误命中
        ...(n.width && n.height ? {
          width: n.width,
          height: n.height,
        } : {}),
        ...(hasValidParent ? { parentId: n.parentNode! } : {}),
        ...(hasValidParent && n.extent ? { extent: n.extent } : {}),
        ...(hasValidParent && n.expandParent ? { expandParent: n.expandParent } : {}),
      };
    });
  }, []);

  const initialNodes = useMemo(() => buildFlowNodes(nodes), []);

  const [localNodes, setLocalNodes, onNodesChangeLocal] = useNodesState<FlowNode>(initialNodes);
  const [localEdges, setLocalEdges, onEdgesChangeLocal] = useEdgesState<Edge>([]);
  const localNodesRef = useRef(localNodes);
  const localEdgesRef = useRef(localEdges);

  useEffect(() => {
    localNodesRef.current = localNodes;
  }, [localNodes]);

  useEffect(() => {
    localEdgesRef.current = localEdges;
  }, [localEdges]);

  useEffect(() => {
    if (isDraggingRef.current) return;
    // P1-1: 拖拽停止后跳过一次全量重建
    // localNodes 的位置已在拖拽过程中通过 onNodesChangeLocal 更新，
    // nodeStore 的位置通过 updateNodePositions 更新，两者一致，无需重建。
    // 跳过重建可避免 ReactFlow diff 所有节点，让 dragging:false 的更新立即执行。
    if (skipNextNodesRebuildRef.current) {
      skipNextNodesRebuildRef.current = false;
      return;
    }
    // resize 期间跳过全量重建：只更新被 resize 节点的 width/height
    // buildFlowNodes 会为所有节点新建 wrapper 对象 → ReactFlow diff 所有节点
    // → backdrop-filter 重新合成 → 频闪
    if (resizingRef.current || isResizingAnyNode.current) {
      // 局部更新：对比 nodes 与 localNodes，只更新尺寸变化的节点
      setLocalNodes(prev => {
        const nodeMap = new Map(nodes.map(n => [n.id, n]));
        let changed = false;
        const next = prev.map(ln => {
          const appNode = nodeMap.get(ln.id);
          if (!appNode) return ln;
          const newWidth = appNode.width;
          const newHeight = appNode.height;
          const newGroupData = isGroupNode(appNode);
          if (newGroupData && (newWidth !== undefined || newHeight !== undefined)) {
            const oldWidth = ln.width;
            const oldHeight = ln.height;
            if (newWidth !== oldWidth || newHeight !== oldHeight) {
              changed = true;
              return {
                ...ln,
                width: newWidth,
                height: newHeight,
                data: { ...appNode.data, nodeWidth: newWidth, nodeHeight: newHeight } as unknown as Record<string, unknown>,
              };
            }
          }
          // 非组节点的 data 引用变化（updateNode 会创建新 data 对象）
          // 只比较 data 引用，引用相同则跳过
          const currentAppNode = ln.data as unknown as AppNode;
          // v1.4.54: 非组节点同步 width/height 到 FlowNode——React Flow 把 node.width 应用为
          // wrapper 内联尺寸，wrapper 尺寸变化才触发 ResizeObserver → updateNodeInternals →
          // handleBounds 重测 → 连线锚点跟随；缺少同步时锚点停留在旧尺寸位置
          const widthChanged = newWidth !== undefined && newWidth !== ln.width;
          const heightChanged = newHeight !== undefined && newHeight !== ln.height;
          if (currentAppNode !== appNode || widthChanged || heightChanged) {
            changed = true;
            return {
              ...ln,
              ...(widthChanged || heightChanged ? {
                width: widthChanged ? newWidth : ln.width,
                height: heightChanged ? newHeight : ln.height,
              } : {}),
              data: appNode as unknown as Record<string, unknown>,
            };
          }
          return ln;
        });
        return changed ? next : prev;
      });
      return;
    }
    const newFlowNodes = buildFlowNodes(nodes, localNodesRef.current);
    setLocalNodes(newFlowNodes);
  }, [nodes, setLocalNodes]);

  // P1-2: 选中变化时只更新 selected 字段，避免全量重建 localNodes
  // 之前 selectedNodeIdsSet 变化会触发 buildFlowNodes 全量重建，导致 ReactFlow diff 所有节点
  // 现在只更新 selected 字段，ReactFlow 只重渲染 selected 变化的节点
  useEffect(() => {
    if (isDraggingRef.current) return;
    setLocalNodes(prev => {
      let changed = false;
      const next = prev.map(n => {
        const isSelected = selectedNodeIdsSet.has(n.id);
        if (n.selected !== isSelected) {
          changed = true;
          return { ...n, selected: isSelected };
        }
        return n;
      });
      return changed ? next : prev;
    });
  }, [selectedNodeIdsSet, setLocalNodes]);
  
  // P0-1: 在 FlowEditor 层计算 activeEdgeIds，对每个选中节点分别 BFS 后合并
  const activeEdgeIds = useMemo(() => {
    if (selectedNodeIds.length === 0) return new Set<string>();
    // 多选场景：合并所有选中节点的连通集合
    const connectedSet = new Set<string>();
    for (const id of selectedNodeIds) {
      const single = getConnectedNodes(id, connections);
      for (const n of single) connectedSet.add(n);
    }
    const active = new Set<string>();
    for (const c of connections) {
      if (connectedSet.has(c.from) && connectedSet.has(c.to)) {
        active.add(connectionEdgeId(c));
      }
    }
    return active;
  }, [selectedNodeIds, connections]);

  // P0-2: 更新 edgeActiveStore，不再通过 data 传入 isActive
  useEffect(() => {
    useEdgeActiveStore.getState().setActiveEdgeIds(activeEdgeIds);
  }, [activeEdgeIds]);

  // 边数据不再包含 isActive，只在 connections 变化时重建
  useEffect(() => {
    const newFlowEdges = connections.map(c => ({
      id: connectionEdgeId(c),
      source: c.from,
      target: c.to,
      targetHandle: c.targetHandle,
      type: 'custom',
    }));
    setLocalEdges(newFlowEdges);
  }, [connections, setLocalEdges]);

  const onNodesChange: OnNodesChange<FlowNode> = useCallback((changes) => {
    const hasPositionChange = changes.some(c => c.type === 'position');
    const isDraggingNow = isDraggingRef.current;

    if (hasPositionChange && isDraggingNow) {
      const positionChanges = changes.filter((c): c is NodePositionChange => c.type === 'position' && 'position' in c && !!c.position);
      for (const c of positionChanges) {
        if (c.position) {
          pendingPositionUpdatesRef.current.set(c.id, c.position);
        }
      }
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          const pending = pendingPositionUpdatesRef.current;
          pendingPositionUpdatesRef.current = new Map();
          const positionChanges = Array.from(pending.entries()).map(([id, position]) => ({
            id,
            type: 'position' as const,
            position,
          }));
          onNodesChangeLocal(positionChanges);
        });
      }
    } else {
      onNodesChangeLocal(changes);
    }
    
    const hasSelectionChange = changes.some(c => c.type === 'select');
    const hasRemove = changes.some(c => c.type === 'remove');

    if (hasSelectionChange) {
      // 同步更新 latestSelectedIdsRef，不依赖 React 渲染周期
      // onSelectionEnd 可能在 localNodes 状态更新前被调用，闭包读取的 localNodes 会过时
      const newSet = new Set(latestSelectedIdsRef.current);
      for (const c of changes) {
        if (c.type === 'select') {
          if (c.selected) {
            newSet.add(c.id);
          } else {
            newSet.delete(c.id);
          }
        }
      }
      latestSelectedIdsRef.current = newSet;
    }

    if (hasSelectionChange && !isSelecting) {
      const selectChanges = changes.filter(c => c.type === 'select');
      const newlySelected = selectChanges.filter((c): c is { id: string; type: 'select'; selected: boolean } => c.selected === true);
      
      if (newlySelected.length === 1) {
        setSelectedNodeIds([newlySelected[0].id]);
      } else if (newlySelected.length > 1) {
        setSelectedNodeIds(newlySelected.map(c => c.id));
      } else {
        const deselectChanges = selectChanges.filter((c): c is { id: string; type: 'select'; selected: boolean } => c.selected === false);
        setSelectedNodeIds(selectedNodeIdsRef.current.filter(id => !deselectChanges.some(c => c.id === id)));
      }
    }
    
    if (hasRemove) {
      for (const change of changes) {
        if (change.type === 'remove') {
          onNodeDelete(change.id);
        }
      }
    }
    
    const hasDragStart = changes.some(c => c.type === 'position' && 'dragging' in c && c.dragging === true);
    const hasDragStop = changes.some(c => c.type === 'position' && 'dragging' in c && c.dragging === false);
    
    if (hasDragStart) {
      // 文档 5.2：普通拖动开始时保存一次快照（复制模式已被 useCopyDrag 拦截，不会进入此处）
      saveHistory?.();
      isDraggingRef.current = true;
      setIsDragging(true);
      // 冻结磁吸系统：拖动中 handle 屏幕坐标缓存过时，防止圆点误吸附飞离节点
      useSelectionStore.getState().setNodeDragging(true);
      window.dispatchEvent(new CustomEvent('flow-drag-start'));
      const dragStartChanges = changes.filter((c): c is NodePositionChange & { dragging: boolean } => c.type === 'position' && 'dragging' in c && c.dragging === true);
      draggedNodeIdsRef.current = dragStartChanges.map((c) => c.id);
    }
    
    if (hasDragStop && isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
      // 恢复磁吸系统（dragStop 提交位置后，MagneticHandle 的 nodeStore 订阅会自动重测缓存）
      useSelectionStore.getState().setNodeDragging(false);
      window.dispatchEvent(new CustomEvent('flow-drag-stop'));
      const positionChanges = changes.filter((c): c is { id: string; type: 'position'; position: { x: number; y: number } } => c.type === 'position' && 'position' in c && !!c.position);
      if (positionChanges.length > 0) {
        const currentNodes = nodesRef.current;
        const nodeMap = new Map(currentNodes.map(n => [n.id, n]));

        // P2-1: 计算所有需要更新的节点位置（包括被拖拽节点和其子节点）
        const positionUpdates: Array<{ id: string; x: number; y: number }> = [];

        // 1. 先处理被拖拽节点本身
        positionChanges.forEach(change => {
          const node = nodeMap.get(change.id);
          if (node && change.position) {
            if (node.parentNode) {
              const parent = nodeMap.get(node.parentNode);
              if (parent) {
                positionUpdates.push({
                  id: change.id,
                  x: change.position.x + parent.x,
                  y: change.position.y + parent.y,
                });
              }
            } else {
              positionUpdates.push({
                id: change.id,
                x: change.position.x,
                y: change.position.y,
              });
            }
          }
        });

        // 2. 处理被拖拽组节点的子节点（子节点跟随组移动）
        const groupDeltas: Map<string, { deltaX: number; deltaY: number }> = new Map();
        positionChanges.forEach(change => {
          const node = nodeMap.get(change.id);
          if (node && isGroupNode(node) && change.position) {
            groupDeltas.set(change.id, {
              deltaX: change.position.x - node.x,
              deltaY: change.position.y - node.y,
            });
          }
        });

        // 为有子节点的组添加子节点位置更新
        if (groupDeltas.size > 0) {
          currentNodes.forEach(n => {
            if (n.parentNode && groupDeltas.has(n.parentNode)) {
              const delta = groupDeltas.get(n.parentNode)!;
              positionUpdates.push({
                id: n.id,
                x: n.x + delta.deltaX,
                y: n.y + delta.deltaY,
              });
            }
          });
        }

        // P2-1: 使用局部更新方法，保持未拖拽节点引用不变
        // 之前 setNodes(finalNodes) 创建全新数组，导致 useInputAssets 的 useShallow 判定不等，
        // 触发所有 GenerateNode 重渲染。现在只更新被拖拽节点，未拖拽节点引用不变。
        useNodeStore.getState().updateNodePositions(positionUpdates);

        // P1-1: 标记跳过下一次 useEffect[nodes] 的全量重建
        // localNodes 的位置已在拖拽过程中通过 onNodesChangeLocal 更新，
        // 此处 updateNodePositions 只更新 nodeStore，不需要重建 localNodes。
        skipNextNodesRebuildRef.current = true;

        // P2-3: 用双 rAF 替代 setTimeout，确保在浏览器空闲时执行
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const currentNodes = useNodeStore.getState().nodes;
            
            draggedNodeIdsRef.current.forEach(nodeId => {
              const node = currentNodes.find(n => n.id === nodeId);
              if (node && !isGroupNode(node)) {
                if (node.groupId) {
                  checkNodeExitGroup(nodeId);
                } else {
                  checkNodeEnterGroup(nodeId);
                }
              }
            });
            
            draggedNodeIdsRef.current = [];
          });
        });
      }
    }
  }, [onNodesChangeLocal, onNodeDelete, setSelectedNodeIds, isSelecting, checkNodeEnterGroup, checkNodeExitGroup, saveHistory]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    // 文档 5.3：删除连接前保存快照
    const hasRemove = changes.some(c => c.type === 'remove');
    if (hasRemove) saveHistory?.();
    onEdgesChangeLocal(changes);
    for (const change of changes) {
      if (change.type === 'remove') {
        const edge = localEdgesRef.current.find(e => e.id === change.id);
        if (edge) {
          useNodeStore.getState().removeConnection(edge.source, edge.target, edge.targetHandle || undefined);
        }
      }
    }
  }, [onEdgesChangeLocal, saveHistory]);

  const onConnect = useCallback((params: Connection) => {
    if (params.source && params.target) {
      // 文档 5.3：添加连接前保存快照
      saveHistory?.();
      useNodeStore.getState().addConnection(params.source, params.target, params.targetHandle || undefined);
    }
  }, [saveHistory]);

  const handleFitView = useCallback(() => {
    reactFlowInstance.fitView({ padding: 0.2 });
  }, [reactFlowInstance]);

  const handleConnectEnd = useCallback((event: MouseEvent | TouchEvent, connectionState: Record<string, unknown>) => {
    console.log('handleConnectEnd triggered', { connectionState, sourceRef: connectingSourceRef.current });
    if (!connectionState.toNode && connectingSourceRef.current) {
      const clientX = 'clientX' in event ? (event as MouseEvent).clientX : 0;
      const clientY = 'clientY' in event ? (event as MouseEvent).clientY : 0;
      console.log('Showing node picker at', clientX, clientY);
      setNodePicker({
        visible: true,
        x: clientX,
        y: clientY,
        sourceNodeId: connectingSourceRef.current,
      });
      connectingSourceRef.current = null;
    }
  }, []);

  const handleConnectStart = useCallback((_event: MouseEvent | TouchEvent, params: { nodeId: string | null; handleId: string | null; handleType: string | null }) => {
    console.log('handleConnectStart triggered', params);
    if (params.handleType === 'source' && params.nodeId) {
      connectingSourceRef.current = params.nodeId;
      console.log('Set connectingSourceRef to', params.nodeId);
    }
  }, []);

  // 画布空白处右键：打开上下文菜单；旧节点选择面板保留给双击/连线落空入口
  const handlePaneContextMenu = useCallback((event: MouseEvent | React.MouseEvent) => {
    event.preventDefault();
    const clientX = 'clientX' in event ? (event as MouseEvent).clientX : 0;
    const clientY = 'clientY' in event ? (event as MouseEvent).clientY : 0;
    if (nodePickerRef.current.visible) {
      setNodePicker({ visible: false, x: 0, y: 0 });
    }
    setCtxMenu({ visible: true, x: clientX, y: clientY });
  }, []);

  // 修复A: handlePaneClick 加定时器，双击时跳过，避免双击触发2次无效的 store 更新
  const handlePaneClick = useCallback(() => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      return; // 双击的第二次 click，跳过
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      if (nodePickerRef.current.visible) {
        setNodePicker({ visible: false, x: 0, y: 0 });
      }
      if (ctxMenuRef.current.visible) {
        setCtxMenu({ visible: false, x: 0, y: 0 });
      }
      setActiveGroup(null);
      useCanvasStore.getState().deselectEdges();
    }, 200);
  }, [setActiveGroup]);

  // 修复B+C: 使用 ReactFlow onPaneDoubleClick，取消残留 click 定时器
  const handleDoubleClick = useCallback((event: MouseEvent | React.MouseEvent) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    // 打开旧选择面板前先收起右键菜单（两面板互斥）
    setCtxMenu({ visible: false, x: 0, y: 0 });
    const clientX = 'clientX' in event ? (event as MouseEvent).clientX : 0;
    const clientY = 'clientY' in event ? (event as MouseEvent).clientY : 0;
    setNodePicker({
      visible: true,
      x: clientX,
      y: clientY,
      sourceNodeId: undefined,
    });
  }, []);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: FlowNode) => {
    if (isGroupNode(node as unknown as AppNode)) {
      setActiveGroup(node.id);
    }
  }, [setActiveGroup]);

  const handleNodeDragStart = useCallback((_: React.MouseEvent, node: FlowNode) => {
  }, []);

  // #3: onSelect 用 ref 间接调用，不再依赖 nodePicker 对象，切断自毁链
  const handleNodePickerSelect = useCallback((type: NodeType) => {
    const picker = nodePickerRef.current;
    const position = reactFlowInstance.screenToFlowPosition({
      x: picker.x,
      y: picker.y,
    });
    const newNodeId = onAddNode(type, position ? { x: position.x, y: position.y } : undefined);
    if (newNodeId && picker.sourceNodeId) {
      useNodeStore.getState().addConnection(picker.sourceNodeId!, newNodeId);
    }
    setNodePicker({ visible: false, x: 0, y: 0 });
  }, [onAddNode, reactFlowInstance]);

  // #2: onClose 用 useCallback，不再内联箭头函数
  const closeNodePicker = useCallback(() => setNodePicker({ visible: false, x: 0, y: 0 }), []);

  const closeCtxMenu = useCallback(() => setCtxMenu({ visible: false, x: 0, y: 0 }), []);

  // 右键菜单选节点：在右键位置新建
  const handleCtxMenuSelect = useCallback((type: NodeType) => {
    const menu = ctxMenuRef.current;
    const position = reactFlowInstance.screenToFlowPosition({ x: menu.x, y: menu.y });
    onAddNode(type, position ? { x: position.x, y: position.y } : undefined);
  }, [onAddNode, reactFlowInstance]);

  // 右键菜单「上传」：复用隐藏 file input，落点为右键位置
  const handleCtxMenuUpload = useCallback(() => {
    const menu = ctxMenuRef.current;
    uploadPositionRef.current = { x: menu.x, y: menu.y };
    pickerFileInputRef.current?.click();
  }, []);

  // 节点菜单「上传」：点击弹出素材选择，选择确定后在菜单位置新建带素材的媒体上传节点
  const handleNodePickerUpload = useCallback(() => {
    // 在弹出文件对话框前保存菜单位置（onClose 随后会清零 nodePicker）
    const picker = nodePickerRef.current;
    uploadPositionRef.current = { x: picker.x, y: picker.y, sourceNodeId: picker.sourceNodeId };
    pickerFileInputRef.current?.click();
  }, []);

  // 侧边栏「上传」（EditorLayout 经 ref 调用）：直接弹出文件选择，
  // 落点用当前视口中心（与粘贴入口一致，保证新节点落在用户视野内），回填复用 handlePickerFileChange
  const handleSidebarUpload = useCallback(() => {
    uploadPositionRef.current = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    pickerFileInputRef.current?.click();
  }, []);

  // 侧边栏建节点聚焦（EditorLayout 经 ref 调用）：视口平滑缩放+平移使新节点居中（模式同打灯面板聚焦）
  // 双 rAF 等新节点首绘测量后再取实测高度（刚入 store 时 measured 尚未回填）；缩放策略：低于 60% 拉到 60%，已高于 60% 维持当前档次
  const focusAnimRef = useRef<number | null>(null);
  const focusStopRef = useRef<(() => void) | null>(null);
  const focusNode = useCallback((nodeId: string) => {
    focusStopRef.current?.();
    const flowEl = document.querySelector<HTMLElement>('.react-flow');
    const W = flowEl?.clientWidth ?? window.innerWidth;
    const H = flowEl?.clientHeight ?? window.innerHeight;
    focusAnimRef.current = requestAnimationFrame(() => {
      focusAnimRef.current = requestAnimationFrame(() => {
        const appNode = useNodeStore.getState().nodes.find((n) => n.id === nodeId);
        if (!appNode) return;
        const measuredHeight = reactFlowInstance.getInternalNode(nodeId)?.measured?.height;
        const { width, height } = getNodeVisualSize(appNode, measuredHeight);
        const from = reactFlowInstance.getViewport();
        const centerX = appNode.x + width / 2;
        const centerY = appNode.y + height / 2;
        // 聚焦缩放：当前低于 60% 时拉到 60%，已高于 60% 则维持（画布 minZoom 0.1，setViewport 不会被钳制）
        const ZOOM_TARGET = Math.max(from.zoom, 0.6);
        const to = { x: W / 2 - centerX * ZOOM_TARGET, y: H / 2 - centerY * ZOOM_TARGET, zoom: ZOOM_TARGET };
        // 目标与当前视口几乎一致时跳过动画，避免无谓的逐帧 store 更新
        if (Math.abs(from.x - to.x) < 0.5 && Math.abs(from.y - to.y) < 0.5 && Math.abs(from.zoom - ZOOM_TARGET) < 0.001) return;
        const stopAnim = () => {
          if (focusAnimRef.current !== null) {
            cancelAnimationFrame(focusAnimRef.current);
            focusAnimRef.current = null;
          }
          flowEl?.removeEventListener('wheel', stopAnim);
          flowEl?.removeEventListener('pointerdown', stopAnim);
        };
        focusStopRef.current = stopAnim;
        // 用户手势优先：动画中滚轮/按下立即让位，避免动画与手势拉扯
        flowEl?.addEventListener('wheel', stopAnim, { passive: true });
        flowEl?.addEventListener('pointerdown', stopAnim);
        const DURATION = 400;
        const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
        let start: number | null = null;
        const step = (now: number) => {
          if (start === null) start = now;
          const t = Math.min(1, (now - start) / DURATION);
          const k = easeOutCubic(t);
          reactFlowInstance.setViewport({
            x: from.x + (to.x - from.x) * k,
            y: from.y + (to.y - from.y) * k,
            zoom: from.zoom + (to.zoom - from.zoom) * k,
          });
          if (t < 1) {
            focusAnimRef.current = requestAnimationFrame(step);
          } else {
            stopAnim();
          }
        };
        focusAnimRef.current = requestAnimationFrame(step);
      });
    });
  }, [reactFlowInstance]);

  // 当前画布可视区中心的画布坐标：取 .react-flow 容器几何中心做 screenToFlowPosition 换算
  // （不用窗口中心近似——画布区被侧边栏/顶栏挤压时窗口中心会偏）；画布未就绪时返回 null 由调用方兜底
  const getViewportCenter = useCallback((): { x: number; y: number } | null => {
    const flowEl = document.querySelector<HTMLElement>('.react-flow');
    const rect = flowEl?.getBoundingClientRect();
    const screenPoint = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const pos = reactFlowInstance.screenToFlowPosition(screenPoint);
    return pos ? { x: pos.x, y: pos.y } : null;
  }, [reactFlowInstance]);

  React.useImperativeHandle(ref, () => ({ openUploadPicker: handleSidebarUpload, focusNode, getViewportCenter }), [handleSidebarUpload, focusNode, getViewportCenter]);

  const handlePickerFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const pos = uploadPositionRef.current;
    const position = reactFlowInstance.screenToFlowPosition({ x: pos.x, y: pos.y });

    // 先创建空的媒体上传节点（占位），保证即时反馈
    const newNodeId = onAddNode(
      NodeType.MEDIA_UPLOAD,
      position ? { x: position.x, y: position.y } : undefined,
    );
    if (newNodeId && pos.sourceNodeId) {
      useNodeStore.getState().addConnection(pos.sourceNodeId, newNodeId);
    }

    const applyAsset = (url: string) => {
      if (!newNodeId) return;
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = findClosestAspectRatio(img.width, img.height);
          useNodeStore.getState().updateNode(newNodeId, {
            image: url,
            videoUri: undefined,
            audioUri: undefined,
            aspectRatio,
            isManualUpload: true,
          });
        };
        img.src = url;
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          const aspectRatio = findClosestAspectRatio(video.videoWidth, video.videoHeight);
          useNodeStore.getState().updateNode(newNodeId, {
            videoUri: url,
            image: undefined,
            audioUri: undefined,
            aspectRatio,
            isManualUpload: true,
          });
        };
        video.src = url;
      } else if (file.type.startsWith('audio/')) {
        useNodeStore.getState().updateNode(newNodeId, {
          audioUri: url,
          image: undefined,
          videoUri: undefined,
          isManualUpload: true,
        });
      }
    };

    const { currentProjectId } = useProjectStore.getState();
    if (currentProjectId) {
      try {
        const asset = await projectApi.uploadAsset(currentProjectId, file, {
          source: 'uploaded',
          node_id: newNodeId || '',
          node_type: NodeType.MEDIA_UPLOAD,
        });
        applyAsset(normalizeAssetUrl(asset.url));
      } catch (err: any) {
        console.error('[节点菜单上传] 上传到后端失败，回退本地:', err.message);
        const reader = new FileReader();
        reader.onload = (ev) => applyAsset(ev.target?.result as string);
        reader.readAsDataURL(file);
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => applyAsset(ev.target?.result as string);
      reader.readAsDataURL(file);
    }

    e.target.value = '';
  }, [onAddNode, reactFlowInstance]);

  // 历史记录素材拖拽：从侧边栏拖入画布，在 drop 位置创建带素材的媒体上传节点
  // 节点根据图片/视频实际尺寸比例显示（复用 findClosestAspectRatio）
  const handleDragOver = useCallback((e: React.DragEvent) => {
    // 允许 drop：侧边栏素材(application/json) 或 系统文件(Files)；两类 dataTransfer 数据互斥，不影响节点拖拽
    if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    // ① 系统文件拖入（资源管理器/桌面）→ 统一上传管线，落点 = 鼠标位置（方案 §4.2）
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      uploadFilesAtScreen(Array.from(e.dataTransfer.files), { x: e.clientX, y: e.clientY }, 'drop');
      return;
    }

    // ② 侧边栏素材拖入（application/json）
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;

    let asset: any;
    try {
      asset = JSON.parse(raw);
    } catch {
      return;
    }
    if (!asset || !asset.src) return;

    // 只处理图片/视频/音频素材（忽略工作流等其他拖拽源）
    const t = typeof asset.type === 'string' ? asset.type : '';
    const isImage = t === 'image' || t.includes('image');
    const isVideo = t === 'video' || t.includes('video');
    const isAudio = t === 'audio' || t.includes('audio');
    if (!isImage && !isVideo && !isAudio) return;

    e.preventDefault();
    e.stopPropagation();

    // 屏幕坐标 → 画布坐标
    const position = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });

    // 先创建空媒体上传节点（占位），保证即时反馈
    const newNodeId = onAddNode(
      NodeType.MEDIA_UPLOAD,
      { x: position.x, y: position.y },
    );
    if (!newNodeId) return;

    // 根据素材类型填充节点数据，并按实际像素尺寸计算 aspectRatio
    if (isImage) {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = findClosestAspectRatio(img.width, img.height);
        useNodeStore.getState().updateNode(newNodeId, {
          image: asset.src,
          videoUri: undefined,
          audioUri: undefined,
          aspectRatio,
          isManualUpload: true,
        });
      };
      img.onerror = () => {
        useNodeStore.getState().updateNode(newNodeId, {
          image: asset.src,
          videoUri: undefined,
          audioUri: undefined,
          isManualUpload: true,
        });
      };
      img.src = asset.src;
    } else if (isVideo) {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const aspectRatio = findClosestAspectRatio(video.videoWidth, video.videoHeight);
        useNodeStore.getState().updateNode(newNodeId, {
          videoUri: asset.src,
          image: undefined,
          audioUri: undefined,
          aspectRatio,
          isManualUpload: true,
        });
      };
      video.onerror = () => {
        useNodeStore.getState().updateNode(newNodeId, {
          videoUri: asset.src,
          image: undefined,
          audioUri: undefined,
          isManualUpload: true,
        });
      };
      video.src = asset.src;
    } else if (isAudio) {
      useNodeStore.getState().updateNode(newNodeId, {
        audioUri: asset.src,
        image: undefined,
        videoUri: undefined,
        isManualUpload: true,
      });
    }
  }, [onAddNode, reactFlowInstance, uploadFilesAtScreen]);

  const handleSelectionStart = useCallback((event: React.MouseEvent | React.PointerEvent) => {
    setIsSelecting(true);
    useSelectionStore.getState().setBoxSelecting(true);
    // 记录框选起点（画布坐标），用于 handleSelectionEnd 重新计算命中节点
    // onSelectionStart 在鼠标移动超过 paneClickDistance 后才触发，此时鼠标位置近似框选起点
    const startPos = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    selectionStartPosRef.current = startPos;
    latestSelectedIdsRef.current = new Set();
  }, [reactFlowInstance]);

  const handleSelectionEnd = useCallback((event: React.MouseEvent | React.PointerEvent) => {
    setIsSelecting(false);
    useSelectionStore.getState().setBoxSelecting(false);

    // 不信任 React Flow 基于 measured 尺寸计算的框选结果
    // measured 可能被浮动 UI（EditorPanel/TopFunctionBar/NodeTitle 等）撑大，导致误命中
    // 这里基于 AppNode 的真实视觉尺寸重新计算框选矩形内的节点
    const startPos = selectionStartPosRef.current;
    const endPos = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    selectionStartPosRef.current = null;

    if (!startPos || !endPos) {
      // fallback：如果无法获取框选矩形，使用 ref 中的结果
      setSelectedNodeIds(Array.from(latestSelectedIdsRef.current));
      return;
    }

    const selectionRect = {
      x: Math.min(startPos.x, endPos.x),
      y: Math.min(startPos.y, endPos.y),
      width: Math.abs(endPos.x - startPos.x),
      height: Math.abs(endPos.y - startPos.y),
    };

    // 基于 AppNode 的位置和视觉尺寸，重新计算框选命中（Partial 模式：任意相交即命中）
    const hitIds = nodesRef.current
      .filter(n => {
        const size = getNodeVisualSize(n, reactFlowInstance.getInternalNode(n.id)?.measured?.height);
        const nodeRect = { x: n.x, y: n.y, width: size.width, height: size.height };
        // Partial 模式：任意相交即命中
        return (
          nodeRect.x < selectionRect.x + selectionRect.width &&
          nodeRect.x + nodeRect.width > selectionRect.x &&
          nodeRect.y < selectionRect.y + selectionRect.height &&
          nodeRect.y + nodeRect.height > selectionRect.y
        );
      })
      .map(n => n.id);

    setSelectedNodeIds(hitIds);
    // 同步 ref，保持一致
    latestSelectedIdsRef.current = new Set(hitIds);
  }, [reactFlowInstance, setSelectedNodeIds]);

  // P0-2：回填模块级 ref（nodeTypes 已模块级化，见 FlowEditorInner 上方的模块级定义）
  callbacksRef.current = { onNodeAction, onNodeUpdate, onNodeDelete, onExtractImage, onAddNode, onGeneratePanorama, onGenerateMultiAngle, onGenerateLighting, onToggleChat, onOpenChat, onOpenDirector, onOpenLayerEdit, onOpenScript, onOpenSmartEdit };

  const multiSelectBounds = useMemo(() => {
    if (selectedNodeIds.length < 2 || isDragging || isSelecting) return null;

    try {
      const rect = reactFlowInstance.getNodesBounds(selectedNodeIds);
      if (!rect || rect.width === 0 || rect.height === 0) return null;

      return {
        canvasX: rect.x,
        canvasY: rect.y,
        canvasWidth: rect.width,
        canvasHeight: rect.height,
      };
    } catch {
      return null;
    }
  }, [selectedNodeIds, reactFlowInstance, isDragging, isSelecting]);

  // 方案 8：onMove → onMoveEnd
  // 移除 60ms debounce 的持续 timer 重置开销，canvasStore 更新频率从 60+ 次/秒降至 1 次/交互
  // 已核查：所有实时性要求高的功能（MagneticHandle、Ctrl+滚轮缩放）都直接订阅 ReactFlow 内部 store，
  // 不依赖 canvasStore.zoom。canvasStore 的 viewport 仅用于显示百分比和 resize 时读取，延迟到 onMoveEnd 不影响功能。
  const handleMoveStart = useCallback(() => {
    // 缩放/平移开始：无需特殊处理
  }, []);

  const handleMoveEnd = useCallback((_event: unknown, viewport: Viewport) => {
    setViewport(viewport.x, viewport.y, viewport.zoom);
  }, [setViewport]);

  // 刷新/切换项目后还原持久化视口：defaultViewport 只在挂载瞬间生效，
  // FlowEditor 已挂载时由 restoreViewport bump viewportSyncCounter 触发此 effect，
  // 将 canvasStore 中的视口（原位置、原缩放比例）直接应用到 ReactFlow 实例，不做 fitView
  const viewportSyncCounter = useCanvasStore((s) => s.viewportSyncCounter);
  useEffect(() => {
    if (viewportSyncCounter === 0) return;
    const { x, y, zoom } = useCanvasStore.getState();
    reactFlowInstance.setViewport({ x, y, zoom }, { duration: 0 });
  }, [viewportSyncCounter, reactFlowInstance]);

  // 流式自适应：原「视口 zoom 钳制」与「viewportScale 比例折算」两个 effect 随
  // 1920 基准缩放体系退役整体删除——画布缩放完全交给用户 Ctrl+滚轮，与首页一致

  return (
    <div className={`w-full h-full ${isDragging ? 'flow-dragging' : ''}`} ref={containerRef} onDoubleClick={handleDoubleClick} onDragOver={handleDragOver} onDrop={handleDrop}>
      <EdgeParticlesCanvas />
      <ReactFlow
        colorMode="dark"
        nodes={localNodes}
        edges={localEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={customNodeTypes}
        edgeTypes={edgeTypes}
        snapToGrid
        snapGrid={[15, 15]}
        connectionRadius={30}
        defaultViewport={initialViewport}
        proOptions={{ hideAttribution: true }}
        onMoveStart={handleMoveStart}
        onMoveEnd={handleMoveEnd}
        selectionOnDrag
        panOnDrag={[1]}
        panOnScroll
        zoomOnDoubleClick={false}
        selectionMode={SelectionMode.Partial}
        minZoom={0.1}
        maxZoom={3}
        onConnectEnd={handleConnectEnd}
        onConnectStart={handleConnectStart}
        onPaneContextMenu={handlePaneContextMenu}
        onPaneClick={handlePaneClick}
        onSelectionStart={handleSelectionStart}
        onSelectionEnd={handleSelectionEnd}
        onlyRenderVisibleElements={false}
        elevateEdgesOnSelect={false}
      >
        <Background color="rgba(200,200,200,0.2)" gap={20} size={2.5} />
        <Panel position="bottom-left" className="!left-4 !bottom-4">
          <MiniMapToolbar />
        </Panel>
        <ViewportPortal>
          {multiSelectBounds && (
            <InverseScaleBox
              fitViewport
              className="absolute -top-[120px] left-1/2 flex justify-center z-30 nodrag"
              style={{
                left: multiSelectBounds.canvasX + multiSelectBounds.canvasWidth / 2,
                top: multiSelectBounds.canvasY - 120,
                transform: 'translateX(-50%)',
                transformOrigin: 'center bottom',
                transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              }}
              scaleOrigin="center bottom"
            >
              <SelectionToolbar
                selectedNodeIds={selectedNodeIds}
                selectionBox={{
                  x: multiSelectBounds.canvasX - 30,
                  y: multiSelectBounds.canvasY - 50,
                  width: multiSelectBounds.canvasWidth + 60,
                  height: multiSelectBounds.canvasHeight + 80,
                }}
              />
            </InverseScaleBox>
          )}
        </ViewportPortal>
      </ReactFlow>
      <NodePickerMenu
        visible={nodePicker.visible}
        x={nodePicker.x}
        y={nodePicker.y}
        onSelect={handleNodePickerSelect}
        onClose={closeNodePicker}
        onUploadClick={handleNodePickerUpload}
      />
      <CanvasContextMenu
        visible={ctxMenu.visible}
        x={ctxMenu.x}
        y={ctxMenu.y}
        onSelectNode={handleCtxMenuSelect}
        onUploadClick={handleCtxMenuUpload}
        onPaste={onPaste || (() => {})}
        onUndo={onUndo || (() => {})}
        onClose={closeCtxMenu}
      />
      <input
        ref={pickerFileInputRef}
        type="file"
        accept="image/*,video/*,audio/*"
        className="hidden"
        onChange={handlePickerFileChange}
      />
      <CopyDragOverlay
        visible={copyDrag.overlayVisible}
        overlayRef={copyDrag.overlayRef}
      />
    </div>
  );
});

export const FlowEditor = memo(React.forwardRef<FlowEditorHandle, FlowEditorProps>((props, ref) => {
  return (
    <ReactFlowProvider>
      <FlowEditorInner {...props} ref={ref} />
    </ReactFlowProvider>
  );
}));